import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { diffUtil } from '../util';
import * as _ from 'underscore';

export interface IDualMultiSelectElement {
  id:number | string;
  name:string;
}

@Component({
  selector: 'dual-multi-select',
  template: `
    <div class="dual-multi-select-container">
      <div class="row">
        <div class="col-7">
          <span *ngIf="label" class="label-text req">{{label}}</span>
        </div>
        <div class="col-5 col-space-bottom" *ngIf="!selectLoading">
          <input type="search" class="form-control" [(ngModel)]="search" [disabled]="filteringDisabled()" [placeholder]="possibleValues?.length ? 'Start typing in to search' : ''">
        </div>
      </div>
      <div class="row" *ngIf="!selectLoading">
        <div class="col-5">
          <select multiple size="10" class="left form-control" [(ngModel)]="currentModel" [disabled]="selectDisabled" (dblclick)="move(currentValues, possibleValues, false)">
            <option *ngFor="let item of currentValues; trackById" [value]="item.id">{{item.name}}</option>
          </select>
        </div>
        <div class="buttons col-2">
          <button type="button" class="btn btn-default text-nowrap" (click)="move(possibleValues, currentValues, true)" title="Add selected" [disabled]="addDisabled()">
            <span class="button-icon-wrapper"><i class="fa fa-chevron-left"></i></span>
            <span *ngIf="!modeRemove" class="button-text">Add selected</span>
            <span *ngIf="modeRemove" class="button-text">Remove selected</span>
          </button>
          <button type="button" class="btn btn-default text-nowrap" (click)="move(currentValues, possibleValues, false)" title="Remove selected" [disabled]="removeDisabled()">
            <span *ngIf="!modeRemove" class="button-text">Remove selected</span>
            <span *ngIf="modeRemove" class="button-text">Undo selected</span>
            <span class="button-icon-wrapper"><i class="fa fa-chevron-right"></i></span>
          </button>
          <p class="separator"></p>
          <button type="button" class="btn btn-default text-nowrap" (click)="move(possibleValues, currentValues, true, true)" title="Add all" [disabled]="addAllDisabled()">
            <span class="button-icon-wrapper"><i class="fa fa-chevron-left double-arrow-margin"></i><i class="fa fa-chevron-left"></i></span>
            <span *ngIf="!modeRemove" class="button-text">Add all</span>
            <span *ngIf="modeRemove" class="button-text">Remove all</span>
          </button>
          <button type="button" class="btn btn-default text-nowrap" (click)="move(currentValues, possibleValues, false, true)" title="Remove all" [disabled]="removeAllDisabled()">
            <span *ngIf="!modeRemove" class="button-text">Remove all</span>
            <span *ngIf="modeRemove" class="button-text">Undo all</span>
            <span class="button-icon-wrapper"><i class="fa fa-chevron-right"></i><i class="fa fa-chevron-right double-arrow-margin"></i></span>
          </button>
        </div>
        <div class="col-5">
          <select multiple size="10" class="right form-control" [(ngModel)]="possibleModel" [disabled]="selectDisabled" (dblclick)="move(possibleValues, currentValues, true)">
            <option *ngFor="let item of possibleValues | filter: search : 'name'; trackById" [value]="item.id">{{item.name}}</option>
          </select>
        </div>
      </div>
    </div>

    <div *ngIf="selectLoading" class="col-space-top col-space-bottom">
      <activity-indicator text="Loading data"></activity-indicator>
    </div>
  `,
  styleUrls: ['./dual-multi-select.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DualMultiSelectComponent implements OnChanges {
  @Input() public modeRemove:boolean;
  @Input() public possibleValues:IDualMultiSelectElement[];
  @Input() public currentValues:IDualMultiSelectElement[];
  @Input() public selectDisabled:boolean;
  @Input() public selectLoading:boolean;
  @Input() public label:string;

  @Output() public onChange:EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public currentValuesChange:EventEmitter<IDualMultiSelectElement[]> = new EventEmitter<IDualMultiSelectElement[]>();

  public currentModel:any[] = [];
  public possibleModel:any[] = [];
  public search:string;

  private originalPossibleValues:IDualMultiSelectElement[];

  constructor() {
   /**/
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.possibleValues) {
      const values:IDualMultiSelectElement[] = changes.possibleValues.currentValue;
      if (values?.length > 0) {
        this.originalPossibleValues = _.clone(values);
      }
      this.possibleValues = values;
      this.sortValues();
    }

    if (changes.currentValues) {
      this.currentValues = changes.currentValues.currentValue;
      this.sortValues();
    }
  }

  public move(from:IDualMultiSelectElement[], to:IDualMultiSelectElement[], add:boolean, all?:boolean):void {
    if (all) {
      this.moveAll(from, to, add);
    } else {
      this.moveSelected(from, to, add);
    }
    this.resetModel();

    // check if there are any changes in the list of possibleValues
    const diff1 = diffUtil.compareArrayOfObjects(this.originalPossibleValues, this.possibleValues);
    const diff2 = diffUtil.compareArrayOfObjects(this.possibleValues, this.originalPossibleValues);
    this.onChange.emit(!(diff1.length === 0 && diff2.length === 0));
    this.sortValues();
  }

  public addDisabled():boolean {
    return this.selectDisabled || this.possibleModel.length === 0;
  }

  public removeDisabled():boolean {
    return this.selectDisabled || this.currentModel.length === 0;
  }

  public addAllDisabled():boolean {
    return this.selectDisabled || this.possibleValues && this.possibleValues.length === 0;
  }

  public removeAllDisabled():boolean {
    return this.selectDisabled || this.currentValues && this.currentValues.length === 0;
  }

  public filteringDisabled():boolean {
    return this.selectDisabled || (this.possibleValues && this.possibleValues.length === 0 && this.search && this.search.length === 0);
  }

  private filter(items:IDualMultiSelectElement[]):IDualMultiSelectElement[] {
    if (this.search && this.search.length > 0) {
      return _.filter(this.possibleValues, this.lookup);
    } else {
      return items;
    }
  }

  private lookup = (possibility:IDualMultiSelectElement):boolean => {
    return possibility.name.toLowerCase().indexOf(this.search.toLowerCase()) !== -1
      || String(possibility.id).toLowerCase().indexOf(this.search.toLowerCase()) !== -1;
  };


  private resetModel():void {
    this.currentModel = [];
    this.possibleModel = [];
  }

  private moveItem(from:IDualMultiSelectElement[], to:IDualMultiSelectElement[], index:number):void {
    to.push(from[ index ]);
    from.splice(index, 1);
  }

  private moveAll(from:IDualMultiSelectElement[], to:IDualMultiSelectElement[], add:boolean):void {
    const filteredFrom:IDualMultiSelectElement[] = add? this.filter(from) : from;
    _.forEach(filteredFrom, e => to.push(e));
    if (filteredFrom.length === from.length) {
      from.length = 0;
    } else {
      _.forEach(filteredFrom, (filtered) => {
        from.splice(
          _.indexOf(from,
            _.find(from, fromItem => this.compare(fromItem.id, filtered.id))
          )
        );
      });
    }
  }

  private moveSelected(from:IDualMultiSelectElement[], to:IDualMultiSelectElement[], add:boolean):void {
    _.forEach(this.selectedItems(add), selectedItem => {
        this.moveItem(from, to,
          _.indexOf(from,
            _.find(from, (fromItem:IDualMultiSelectElement) => this.compare(fromItem.id, selectedItem))
          )
        );
      }
    );
  }

  private compare(id:number | string, item:any):boolean {
    if (_.isString(id)) {
      return String(id) === String(item);
    } else if (_.isNumber(id)) {
      return Number(id) === Number(item);
    }
    return false;
  }

  private selectedItems(add:boolean):any[] {
    return add? this.possibleModel : this.currentModel;
  }

  private sortValues():void {
    // refresh sort array based on name
    this.possibleValues = _.sortBy(this.possibleValues, 'name');
    this.currentValues = _.sortBy(this.currentValues, 'name');
    this.currentValuesChange.emit(this.currentValues);
  }
}